// frontend/src/pages/UploadPage/UploadPage.js
// Purpose: Main container for file upload functionality

// Desired outcome: Provide a user-friendly interface for uploading, viewing, 
// and managing files with clear feedback on operations

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUploadedFiles } from '../../state/analysis/analysisSlice';
import DragDropZone from '../../components/DragDropZone/DragDropZone';
import FileList from '../../components/FileList/FileList';
import logger from '../../utils/logger';
import './UploadPage.css';

const UploadPage = () => {
  const dispatch = useDispatch();
  const { uploadedFiles, error, progress } = useSelector(state => state.analysis);

  // Report error if one exists
  useEffect(() => {
    if (error) {
      setMessage({ type: 'error', text: error || 'An error occurred while fetching files' });
      logger.error('Upload page error:', error);
    }
  }, [error]);

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch uploaded files on component mount
  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchUploadedFiles())
      .unwrap()
      .then(() => {
        setIsLoading(false);
        logger.info('Files loaded successfully');
      })
      .catch(err => {
        setIsLoading(false);
        setMessage({ type: 'error', text: err.message || 'Error fetching files' });
        logger.error('Error loading files', err);
      });
  }, [dispatch]);

  // Handle successful file upload
  const handleUploadSuccess = () => {
    setMessage({ type: 'success', text: 'File uploaded successfully!' });
    
    // Re-fetch the file list to show the newly uploaded file
    dispatch(fetchUploadedFiles());
    
    // Clear message after 5 seconds
    setTimeout(() => {
      setMessage(null);
    }, 5000);
  };

  // Handle file upload error
  const handleUploadError = (errorMsg) => {
    setMessage({ type: 'error', text: errorMsg || 'Error uploading file' });
    
    // Clear message after 5 seconds
    setTimeout(() => {
      setMessage(null);
    }, 5000);
  };

  return (
    <div className="upload-page">
      <h1>Upload Files</h1>
      
      {/* Status message display */}
      {message && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}
      
      {/* Drag and drop upload zone */}
      <section className="card">
        <h2 className="card-title">Upload New File</h2>
        <DragDropZone 
          onUploadSuccess={handleUploadSuccess} 
          onUploadError={handleUploadError}
        />
      </section>
      
      {/* File listing section */}
      <section className="card">
        <h2 className="card-title">Uploaded Files</h2>
        <FileList 
          files={uploadedFiles} 
          isLoading={isLoading || progress.status === 'loading'}
          onDeleteSuccess={() => {
            setMessage({ type: 'success', text: 'File deleted successfully!' });
            setTimeout(() => setMessage(null), 5000);
          }}
          onDeleteError={(err) => {
            setMessage({ type: 'error', text: err || 'Error deleting file' });
            setTimeout(() => setMessage(null), 5000);
          }}
        />
      </section>
    </div>
  );
};

export default UploadPage;